import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { REVENUE_DATA_SOURCE_OPTIONS } from "constants/revenueDataSource";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import { useResyncInvoiceMutation } from "store/apis/invoices";
import { Invoice } from "types/Models/invoice";
import { BackendError } from "types/utils/error";

type ResyncInvoiceModalProps = {
  invoice: Invoice;
  open: boolean;
  onClose: () => void;
};

export const ResyncInvoiceModal = ({
  invoice,
  open,
  onClose,
}: ResyncInvoiceModalProps) => {
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const [resyncInvoice, { isLoading }] = useResyncInvoiceMutation();
  const sourceName = REVENUE_DATA_SOURCE_OPTIONS[invoice.source];

  const handleResync = async () => {
    try {
      await resyncInvoice({ entityId, invoiceId: invoice.uuid }).unwrap();
      successToast({
        message: `${invoice.invoice_number} resynced from ${sourceName}.`,
      });
      onClose();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Something went wrong",
      });
    }
  };

  return (
    <Modal.Root open={open} onOpenChange={onClose}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Resync invoice from {sourceName}</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body className="t-flex t-flex-col t-gap-4 t-text-body-sm t-text-text-60">
          <p className="t-m-0 t-text-text-100">
            The latest version of <b>{invoice.invoice_number}</b> will be
            fetched from {sourceName} and its journal entries rebuilt.
          </p>
          <div className="t-flex t-flex-col t-gap-1">
            <span className="t-font-medium t-text-text-100">
              What will happen
            </span>
            <ul className="t-m-0 t-pl-5 t-list-disc t-flex t-flex-col t-gap-1">
              <li>
                The invoice amount, dates, status and line items are replaced
                with the data in {sourceName}.
              </li>
              <li>
                Its current journal entries are deleted and new ones are created
                from the latest data.
              </li>
            </ul>
          </div>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Modal.RawClose asChild>
            <Button type="button">Cancel</Button>
          </Modal.RawClose>
          <Button
            customType="primary"
            onClick={handleResync}
            isLoading={isLoading}
            disabled={isLoading}
          >
            Resync
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
